import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AxiosError, CancelTokenSource } from "axios";
import axios from "axios";
import type { detailedChargerDataInterface } from "../types/chargerTypes";
import dayjs from "dayjs";
import type { NewApiResponse, detailedGeneratorData } from "../types/powerTypes";
import { axiosInstance } from "../../utils/common";
import type { KwhData } from "../types";

/**
 * DashboardService - Handles API calls for dashboard-related data
 * - Charger list data (customer-level and site-level)
 * - Generator/power data (customer-level and site-level)
 * - Factory dashboard aggregate data
 */

interface FetchSessionsPayload2 {
    fromDate?: string;
    toDate?: string;
    selectedCustomerName: string;
    site_id?: number
}

let cancelTokenSource11: CancelTokenSource | null = null;
let chargerPollingInterval: NodeJS.Timeout | null = null;

// Async thunk to fetch detailed summary charger data
export const fetchdetailedSummaryCharger = createAsyncThunk<
    detailedChargerDataInterface, // Return type
    FetchSessionsPayload2, // Argument type
    { rejectValue: string } // Optional reject value
>(
    'sites/fetchdetailedSummaryCharger',
    async (payload: FetchSessionsPayload2, thunkAPI) => {
        const { fromDate, toDate, selectedCustomerName } = payload;

        // If there's already an ongoing request, cancel it
        if (cancelTokenSource11) {
            cancelTokenSource11.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSource11 = axios.CancelToken.source();

        const fromrange = fromDate ?? dayjs().subtract(7, 'day').toISOString();
        const torange = toDate ?? dayjs().toISOString();

        try {
            // API call
            const response = await axiosInstance.get<any>(
                `charger/list/`,
                {
                    headers: {
                        customer: selectedCustomerName,
                    },
                    cancelToken: cancelTokenSource11.token, // Use the new cancel token
                }
            );
            if (response.status === 200) {
                startPollingChargerSummary(payload, thunkAPI.dispatch);
            }

            // Transform response to match expected interface
            const transformedData: detailedChargerDataInterface = {
                session: [],
                connector: [],
                all_connected_chargers: Array.isArray(response.data) ? response.data : []
            };

            return transformedData;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled'); // Optional: you can return an empty array or specific error message
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

// Function to start polling
export const startPollingChargerSummary = (
    payload: FetchSessionsPayload2,
    dispatch: any,
    interval: number = 300000  // Default 3 minutes
) => {

    // Clear any existing polling
    stopPollingChargerSummary();

    // Start a new polling interval
    chargerPollingInterval = setInterval(() => {
       
        dispatch(fetchdetailedSummaryCharger(payload));
        dispatch(fetchSessionsForCustomer(payload));
    }, interval);

    // Immediately fetch data before the first interval
    // dispatch(fetchdetailedSummaryCharger(payload));
};

// Function to stop polling
export const stopPollingChargerSummary = () => {
  
    if (chargerPollingInterval) {
        clearInterval(chargerPollingInterval);
        chargerPollingInterval = null;
    }
    if (cancelTokenSource11) {
        cancelTokenSource11.cancel('Request canceled');
        cancelTokenSource11 = null;
    }
};

let cancelTokenSource12: CancelTokenSource | null = null;

export const fetchdetailedSummaryChargerForSite = createAsyncThunk<
    detailedChargerDataInterface, // Return type (array of SessionData)
    FetchSessionsPayload2, // Argument type
    { rejectValue: string } // Optional reject value
>(
    'sites/fetchdetailedSummaryChargerForSite',
    async (payload: FetchSessionsPayload2, thunkAPI) => {
        const { fromDate, toDate, selectedCustomerName, site_id } = payload;

        // If there's already an ongoing request, cancel it
        if (cancelTokenSource12) {
            cancelTokenSource12.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSource12 = axios.CancelToken.source();

        const fromrange = fromDate ?? dayjs().subtract(1, 'day').toISOString();
        const torange = toDate ?? dayjs().toISOString();

        try {
            // API call
            const { data } = await axiosInstance.get<any>(
                `charger/list/${site_id}/`,
                {
                    headers: {
                        customer: selectedCustomerName,
                    },
                    cancelToken: cancelTokenSource12.token, // Use the new cancel token
                }
            );

            // Transform response to match expected interface
            const transformedData: detailedChargerDataInterface = {
                session: [],
                connector: [],
                all_connected_chargers: Array.isArray(data) ? data : []
            };

            return transformedData;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled'); // Optional: you can return an empty array or specific error message
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

let cancelTokenSourceSessions: CancelTokenSource | null = null;

// Async thunk to fetch sessions for customer
export const fetchSessionsForCustomer = createAsyncThunk<
    any[], // Return type - array of session objects
    FetchSessionsPayload2, // Argument type
    { rejectValue: string } // Optional reject value
>(
    'sites/fetchSessionsForCustomer',
    async (payload: FetchSessionsPayload2, thunkAPI) => {
        const { fromDate, toDate, selectedCustomerName } = payload;
     

        // If there's already an ongoing request, cancel it
        if (cancelTokenSourceSessions) {
         
            cancelTokenSourceSessions.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSourceSessions = axios.CancelToken.source();

        const fromrange = fromDate ?? dayjs().subtract(2, 'day').toISOString();
        const torange = toDate ?? dayjs().toISOString();
       

        try {
            const response = await axiosInstance.get<any>(
                `charger/sessions/`,
                {
                    params: {
                        from_date: fromrange,
                        to_date: torange,
                    },
                    headers: {
                        customer: selectedCustomerName,
                    },
                    cancelToken: cancelTokenSourceSessions.token,
                }
            );

         
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            if (axios.isCancel(error)) {
                console.warn('[fetchSessionsForCustomer] CANCELLED by cancel token');
                return thunkAPI.rejectWithValue('Request canceled');
            }
            console.error('[fetchSessionsForCustomer] ERROR', (error as AxiosError).message);
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

let cancelTokenSourceEqpCust: CancelTokenSource | null = null;
// Define payload type
export type FetchBPLiveDataPayloadEqpCust = {
    selectedCustomerName: string;
};

// Async thunk to fetch BP live data for customer
export const fetchBPEqpCustkWh = createAsyncThunk<
    KwhData, // Return type
    FetchBPLiveDataPayloadEqpCust, // Argument type
    { rejectValue: string }  // Optional reject value
>(
    'sites/fetchBPEqpCustkWh',
    async (payload, thunkAPI) => {
        const { selectedCustomerName } = payload;

        // Cancel ongoing request if any
        if (cancelTokenSourceEqpCust) {
            cancelTokenSourceEqpCust.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSourceEqpCust = axios.CancelToken.source();

        try {
            const response = await axiosInstance.post<KwhData>(
                `trend/monthly_kWh_customer/`,
                {
                    source_id: 'bluepillar',
                },
                {
                    headers: {
                        customer: selectedCustomerName
                    },
                    cancelToken: cancelTokenSourceEqpCust.token, // Use the new cancel token
                }
            );
            return response.data;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled');
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

let cancelTokenSource: CancelTokenSource | null = null;
let bpPollingInterval: NodeJS.Timeout | null = null;

// Define payload type
export type FetchBPLiveDataPayload2 = {
    selectedCustomerName: string;
    site_id?: number;
};

// Async thunk to fetch BP live data for customer
export const fetchBPLiveDataForCustomer = createAsyncThunk<
    detailedGeneratorData[], // Return type
    FetchBPLiveDataPayload2, // Argument type
    { rejectValue: string }  // Optional reject value
>(
    'sites/fetchBPLiveDataForCustomer',
    async (payload, thunkAPI) => {
        const { selectedCustomerName } = payload;

        // Cancel ongoing request if any
        if (cancelTokenSource) {
            cancelTokenSource.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSource = axios.CancelToken.source();

        try {
            const response = await axiosInstance.get<detailedGeneratorData[]>(
                `trend/live_values_v3`,
                {
                    headers: {
                        customer: selectedCustomerName,
                    },
                    cancelToken: cancelTokenSource.token, // Use the new cancel token
                }
            );
            if (response.status === 200) {
                startPollingBPLiveData(payload, thunkAPI.dispatch);
            }
            return response.data;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled');
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

// Function to start polling
export const startPollingBPLiveData = (
    payload: FetchBPLiveDataPayload2,
    dispatch: any,
    interval: number = 300000 // Default 5 minutes
) => {
    // Clear any existing polling
    stopPollingBPLiveData();

    // Start a new polling interval
    bpPollingInterval = setInterval(() => {
        dispatch(fetchBPLiveDataForCustomer(payload));
        dispatch(fetchBPEqpCustkWh(payload));
    }, interval);

    // Immediately fetch data before the first interval
    // dispatch(fetchBPLiveDataForCustomer(payload));
};

// Function to stop polling
export const stopPollingBPLiveData = () => {
    if (bpPollingInterval) {
        clearInterval(bpPollingInterval);
        bpPollingInterval = null;
    }
    if (cancelTokenSource) {
        cancelTokenSource.cancel('Request canceled');
        cancelTokenSource = null;
    }
};

let cancelTokenSource1: CancelTokenSource | null = null;

export const fetchBPLiveDataForCustomerForOneSite = createAsyncThunk<detailedGeneratorData[], FetchBPLiveDataPayload2, { rejectValue: string }>(
    'sites/fetchBPLiveDataForCustomerForOneSite',
    async (payload, thunkAPI) => {
        const { selectedCustomerName, site_id } = payload;

        if (cancelTokenSource1) {
            cancelTokenSource1.cancel('Request canceled');
        }

        cancelTokenSource1 = axios.CancelToken.source();

        try {
            const { data } = await axiosInstance.get<detailedGeneratorData[]>(
                `trend/live_values_v3?siteId=${site_id}`,
                {
                    headers: {
                        'customer': selectedCustomerName,
                    },
                    cancelToken: cancelTokenSource1.token,
                }
            );
            return data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

let cancelTokenSource7: CancelTokenSource | null = null;
let bpPollingInterval7: NodeJS.Timeout | null = null;

// Define payload type
export type FetchBPLiveDataPayload7 = {
    schemas: string[];
};

// Function to stop polling
export const stopPollingBPLiveDataFactory = () => {
    if (bpPollingInterval7) {
        clearInterval(bpPollingInterval7);
        bpPollingInterval7 = null;
    }
    if (cancelTokenSource7) {
        cancelTokenSource7.cancel('Request canceled');
        cancelTokenSource7 = null;
    }
};


let cancelTokenSource2: CancelTokenSource | null = null;

// Async thunk to fetch BP live data for customer
export const fetchFactoryApiResponse = createAsyncThunk<
    NewApiResponse[], // Return type
    void, // Argument type
    { rejectValue: string }  // Optional reject value
>(
    'sites/fetchFactoryApiResponse',
    async (_, thunkAPI) => {

        // Cancel ongoing request if any
        if (cancelTokenSource2) {
            cancelTokenSource2.cancel('Request canceled due to new request');
        }

        // Create a new cancel token for the current request
        cancelTokenSource2 = axios.CancelToken.source();

        try {
            const response = await axiosInstance.get<NewApiResponse[]>(
                `trend/factory_api`,
                {
                    cancelToken: cancelTokenSource2.token, // Use the new cancel token
                }
            );
            return response.data;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled');
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);